import dayjs from 'dayjs';
import {
  action,
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from 'mobx';

import {pollAsync} from '@yourcoach/async-utils';

import type {UserModel} from '../../core/domain/User/User.model';
import type {CoreOrB2b, CoreOrB2bEnv} from '../../utils/coreOrB2b/types';
import type {AccountApiModel} from '../account';
import type {ApiListResponse} from '../types';
import type {
  BNUserProfileBecomeCoachParams,
  BNUserProfileUpdateParams,
} from '../types/bnrks/methods';
import type {
  CoreUserAccountsListParams,
  CoreUserExternalAuthGetAuthenticationUrlParams,
  CoreUserProfileBecomeCoachParams,
  CoreUserProfileGetProviderUrlParams,
  CoreUserProfileReadParams,
  CoreUserProfileUpdateParams,
  CoreUserTosAcceptParams,
  CoreUserTosReadParams,
} from '../types/core/methods';
import type {Role, UserExpandedCommon} from './types';

import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {getUserSource} from '../../utils/entity/user/getUserSource';
import {envManager} from '../../utils/env';
import {logger} from '../../utils/logger';
import {storage} from '../../utils/storage';
import {isTestEnv} from '../../utils/test-utils/isTestEnv';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';
import {userExpandCommon} from './expand';

export const STORAGE_KEY_CURRENT_USER = 'current_user';

export class UserStore<T extends UserExpandedCommon = UserExpandedCommon> {
  updating: {
    error: any;
    inProgress: boolean;
    success: boolean;
  } = {
    error: null,
    inProgress: false,
    success: false,
  };
  user: null | T = null;
  private _store = new EntityStore<T, CoreUserProfileReadParams>('user', {
    fetch: 'user.profile.read',
  });

  constructor() {
    makeObservable(this, {
      clear: !isTestEnv ? action.bound : false,
      fetch: !isTestEnv ? action.bound : false,
      hasArticlesAccess: computed,
      isCrmUser: computed,
      localUpdate: !isTestEnv ? action.bound : false,
      setUser: !isTestEnv ? action.bound : false,
      source: computed,
      updating: observable,
      user: observable,
    });

    autorun(() => {
      if (this.user) {
        storage.setItem(this.getStorageKey(), JSON.stringify(toJS(this.user)));
      }
    });
  }

  get hasArticlesAccess() {
    if (this.user?.roles.includes('coach')) {
      return this.user?.coach_subtenants?.some(
        s => (s?.subscribed_to?.length || 0) > 0,
      );
    }

    return (this.user?.subtenant?.subscribed_to?.length || 0) > 0;
  }

  get isCrmUser() {
    return (
      this.user?.subtenant?.features.widget_flow?.ux_type === 'crm' ||
      this.user?.subtenant?.features.widget_flow?.ux_type === 'one_call'
    );
  }

  get isFetching() {
    return this._store.fetching.inProgress;
  }

  get source(): UserModel.UserSource {
    return getUserSource(this.user);
  }

  __getStorageKeyForEnv(env: CoreOrB2bEnv) {
    const prefix = this.getStorageKeyPrefixForEnv(env);

    return `${prefix}${STORAGE_KEY_CURRENT_USER}`;
  }

  async _restoreUser() {
    try {
      if (this.user) {
        return true;
      }

      const user = JSON.parse(await storage.getItem(this.getStorageKey())) as T;

      if (user) {
        this.setUser(user);

        this.fetch();

        return true;
      }

      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error restoring current user', error);
      }

      storage.removeItem(this.getStorageKey());

      return false;
    }
  }

  async acceptTos() {
    const {user}: {user: T} = await apiRequest<CoreUserTosAcceptParams>({
      method: 'user.tos.accept',
    });

    await this.localUpdate({
      tos_accepted: user.tos_accepted,
      tos_version: user.tos_version,
    } as Partial<T>);

    return this.user;
  }

  async becomeCoach({
    b2b,
    core,
  }: {
    b2b?: BNUserProfileBecomeCoachParams;
    core: CoreUserProfileBecomeCoachParams;
  }) {
    const {user}: {user: T} = await apiRequest<
      BNUserProfileBecomeCoachParams | CoreUserProfileBecomeCoachParams
    >({
      method: 'user.profile.become_coach',
      params: pickCoreOrB2b<
        CoreUserProfileBecomeCoachParams,
        BNUserProfileBecomeCoachParams
      >({b2b, core}),
    });

    if (!isTestEnv) {
      // refresh token here
      // because backend checks coach role from the token

      // @ts-expect-error: see auth/store.ts:140
      await this.__ensureAccessTokenIsNotRefreshing();

      // @ts-expect-error: see auth/store.ts:133
      await this.__renewAccessToken();
    }

    await this.localUpdate({
      coach_status: user.coach_status,
      roles: [...this.user!.roles, 'coach'],
    } as Partial<T>);

    return this.user;
  }

  clear() {
    this.user = null;

    storage.removeItem(this.getStorageKey());
  }

  async disconnectGoogleAccount() {
    await apiRequest({
      method: 'user.external_auth.revoke_access',
    });

    await this.fetch();
  }

  async fetch(
    {expand = userExpandCommon} = {
      expand: userExpandCommon,
    },
  ) {
    try {
      const user: T = await this._store.fetch({expand});

      runInAction(() => {
        this.user = user;
      });
    } catch (error) {
      if (!apiRequest.isCanceledError(error)) {
        return;
      }

      throw error;
    }
  }

  async fillOnboardingDataForCrmUserIfNeeded() {
    try {
      await pollAsync(() => !this.isFetching);

      await pollAsync(() => Boolean(this.user));

      if (!this.user) {
        return;
      }

      if (this.isCrmUser && !this.user.onboarding?.finished) {
        const now = dayjs().toISOString();

        const params: CoreUserProfileUpdateParams = {
          metadata: {
            ...this.user.metadata,
            onboarding: {
              account_type: 'b2b_client',
              finished: now,
              is_finished: true,
            },
          },
          onboarding: {
            answers: this.user.onboarding?.answers ?? [],
            finished: now,
            type: 'internal',
          },
        };

        await this.update({
          core: params,
        });
      }
    } catch (error) {
      logger.error(error);
    }
  }

  getGoogleAccountEmail() {
    return this.user?.external_auth_status?.email ?? null;
  }

  async getLoginGoogleUrl() {
    const {url}: {url: string} =
      await apiRequest<CoreUserExternalAuthGetAuthenticationUrlParams>({
        method: 'user.external_auth.get_authentication_url',
      });

    return url;
  }

  async getProviderUrl(
    {
      isExpress = true,
      toWebsite = false,
    }: {
      isExpress?: boolean;
      toWebsite?: boolean;
    } = {
      isExpress: true,
      toWebsite: false,
    },
  ) {
    let {url}: {url: string} =
      await apiRequest<CoreUserProfileGetProviderUrlParams>({
        method: 'user.profile.get_provider_url',
        params: {
          is_express: isExpress,
          to_website: toWebsite,
        },
      });

    if (!isExpress && !url) {
      url = 'https://dashboard.stripe.com/login';
    }

    return url;
  }

  getStorageKey() {
    return this.__getStorageKeyForEnv(envManager.get('isB2b') ? 'b2b' : 'core');
  }

  async getTosVersion() {
    const {
      tos: {version},
    }: {tos: {version: number}} = await apiRequest<CoreUserTosReadParams>({
      method: 'user.tos.read',
    });

    return version;
  }

  async localUpdate(data: Partial<T> & {[key: string]: any}) {
    const updatedUser = {
      ...this.user,
      ...(data as T),
    };

    this.user = updatedUser;

    return updatedUser;
  }

  setUser(user: T) {
    const roles: Role[] = [];

    // TODO: Should we remove this? It seems was needed for migration
    if (!user.roles) {
      // @ts-expect-error: TODO: describe why
      if (user.is_coach) {
        roles.push('coach');
      }

      // @ts-expect-error: TODO: describe why
      if (user.is_mentor) {
        roles.push('mentor');
      }

      // @ts-expect-error: TODO: describe why
      if (user.is_admin) {
        roles.push('admin');
      }
    }

    this.user = {
      ...user,
      // TODO: Remove this
      // Counters migration
      counters: {
        ...(user.counters || {}),
        followers: (user.counters || {}).followers || {
          invited: 0,
          // @ts-expect-error: TODO: describe why
          joined: user.followers_count || 0,
          matched: 0,
          requested: 0,
        },
        following: (user.counters || {}).following || {
          invited: 0,
          // @ts-expect-error: TODO: describe why
          joined: user.following_count || 0,
          matched: 0,
          requested: 0,
        },
      },
      // Roles migration
      roles: user.roles || roles,
    };
  }

  async subscribeNewsletter(isCoach = false) {
    const accountsResult: ApiListResponse<AccountApiModel> =
      await apiRequest<CoreUserAccountsListParams>({
        method: 'user.accounts.list',
        params: {
          query: [['type', '==', 'email']],
        },
      });

    const hasEmailAccount = accountsResult._items.length;

    let success = false;
    let error = '';

    if (hasEmailAccount) {
      const data = new FormData();

      data.append('email', accountsResult._items[0]!.value);
      data.append('name', this.user!.name);

      const endpoint =
        isCoach || this.user!.roles.includes('coach')
          ? 'https://yourcoach.health/wp-content/themes/yourcoach/reactApp/mailChimpCoachForm.php'
          : 'https://yourcoach.health/wp-content/themes/yourcoach/reactApp/mailChimpForm.php';

      const result = await fetch(endpoint, {
        body: data,
        method: 'POST',
        mode: 'no-cors',
      });

      error = result.statusText;

      if (result.ok) {
        success = true;
      }
    } else {
      error = 'No email account';
    }

    return {error, success};
  }

  async update({
    b2b,
    core,
  }: CoreOrB2b<CoreUserProfileUpdateParams, BNUserProfileUpdateParams>) {
    try {
      await pollAsync(() => !this.updating.inProgress);

      runInAction(() => {
        this.updating = {
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const params = pickCoreOrB2b<
        CoreUserProfileUpdateParams,
        BNUserProfileUpdateParams
      >({
        b2b,
        core,
      });

      const result = await apiRequest<
        BNUserProfileUpdateParams | CoreUserProfileUpdateParams
      >({
        method: 'user.profile.update',
        params: {
          expand: userExpandCommon,
          ...params,
        },
      });

      const updatedUser = expandObj<T>(result.user, {
        expand: params?.expand || userExpandCommon,
        expanded: result._expanded,
      });

      runInAction(() => {
        this.updating = {
          error: null,
          inProgress: false,
          success: true,
        };

        this.user = updatedUser;
      });

      return updatedUser;
    } catch (error) {
      this.updating = {
        error,
        inProgress: false,
        success: false,
      };

      throw error;
    }
  }

  async userExists() {
    return this._restoreUser();
  }

  private getStorageKeyPrefixForEnv(env: CoreOrB2bEnv) {
    if (env === 'b2b') {
      return 'b2b_';
    }

    return '';
  }
}

export const currentUserStore = new UserStore();
